/* eslint-disable complexity */
import React, { CSSProperties, FC, memo, useContext, useRef } from 'react';
import clsx from 'clsx';
import { capitalize } from 'lodash';
import { useLocation } from 'react-router-dom';

import { ReportAccount } from '@futrli-sdk/predict';
import { ROUTES } from '@futrli/shared-services';

import { AccountRow, CalculationRow, GroupRow, OrganisationRow, TotalRow } from '../types';
import { ForecastType } from '../../../../../forecast/onboarding/shared/onboarding-type-select';
import { getRowHeightSize } from '../../user-grid-settings/user-grid-settings-actions';
import { HeaderRowAccountTitle } from './header-row-account-title';
import { MARGIN_LEFT_LEVEL } from './header-row-utils';
import { nestedAccountHeightOverride } from '../style-rows';
import { PredictManagementContext } from '../../../../../predict-management/context/predict-management-context';
import { ROW_KIND } from '../constants';
import {
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../../../forecast/onboarding/const';
import { SIZES } from '../../../../../core/constants';
import { useOnboardingUrlInfo } from '../../../../../forecast/onboarding/utils/hooks';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { UserGridSettingsContext } from '../../user-grid-settings/user-grid-settings-context';

import grid from '../../../../../ui/data-grid/components/grid.scss';
import * as styles from './header-row.scss';

interface Props {
  level: number;
  row: TotalRow | AccountRow | CalculationRow | GroupRow | OrganisationRow;
  wrapperStyle: CSSProperties;
  isPayments: boolean;
}

export const HeaderRowAccount: FC<Props> = memo(({ level, row, wrapperStyle }) => {
  const {
    isBold,
    childCount,
    kind,
    account: { index },
    isFirst,
    isLast,
    forceRowSize,
    isMainHeadline
  } = row;

  const { headerBackground } = row as TotalRow;

  const {
    isOnboardingBudgetRoute,
    isOnboardingScenarioRoute,
    onboardingStep,
    forecastType,
    isOnboardingScenarioWithInvoicesRoute
  } = useOnboardingUrlInfo();

  const location = useLocation();
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { forecast, isConsolidation } = usePredictionsInfoContext();
  const { expandedRows, rowHeights } = useContext(UserGridSettingsContext);

  const refGroupingAccountSelector = useRef<HTMLDivElement>(null);

  const rowHeight = getRowHeightSize(rowHeights);

  if (!forecast) {
    addErrorMessage?.(new Error('Missing forecast or report required to render HeaderRowAccount'));
    return null;
  }

  const isExpandedRow = (key: string | number) =>
    expandedRows.includes(key) || (row.kind === ROW_KIND.total && row.forceRowSize === 'medium');

  const assumptionsRowHeightOverride = nestedAccountHeightOverride(row, rowHeight, level + 1);
  const isAccountRow = kind === ROW_KIND.account;
  const hasChild = childCount >= 1;
  const expandedRow = isExpandedRow(index) && hasChild;
  const isNotLevel0 = level > 0;

  const headerRowClasses = clsx({
    [grid.rowHeader]: true,
    [grid.isChild]: isNotLevel0,
    [grid.isExpanded]: expandedRow,
    [grid.isFirst]: isFirst,
    [grid.isLast]: isLast,
    [grid[`is${capitalize(kind)}`]]: true,
    [grid.hasChild]: hasChild,
    [grid.hasForceRowSize]: Boolean(forceRowSize),
    [grid.isMainHeadline]: Boolean(isMainHeadline)
  });

  const headerRowInnerClasses = clsx({
    [grid.isBold]: isBold,
    [grid.rowHeaderInner]: true,
    [grid[assumptionsRowHeightOverride]]: true,
    [grid.outdented]: hasChild && !(row.kind === ROW_KIND.total && row.forceRowSize === 'medium'),
    [grid.forceSmallContent]: Boolean(forceRowSize) && rowHeight === SIZES.SMALL
  });

  const averageDaysToPay =
    typeof (row as AccountRow).account.average_days_to_pay === 'number'
      ? (row as AccountRow).account.average_days_to_pay
      : 0;

  const isBalanceSheetPage = location.pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;
  const isAssetsOrLiabilitiesPage =
    location.pathname.indexOf(ROUTES.ASSETS) !== -1 ||
    location.pathname.indexOf(ROUTES.LIABILITIES) !== -1;

  const showDeferVat = (row.account as ReportAccount).system_account_role === 'vat' && isBalanceSheetPage;

  const showDaysToPay =
    !isConsolidation && isAccountRow && forecast.kind !== 'budget' && !isBalanceSheetPage && !isAssetsOrLiabilitiesPage && !isOnboardingBudgetRoute;

  const hideDaysToPayValue =
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.FromScratch &&
      Number(onboardingStep) < Number(SCENARIO_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      Number(onboardingStep) < Number(SCENARIO_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioRoute &&
      forecastType === ForecastType.FromLYA &&
      Number(onboardingStep) < Number(SCENARIO_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.FromScratch &&
      Number(onboardingStep) < Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SCRATCH_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.WithAutoPredictions &&
      Number(onboardingStep) < Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.AUTO_PREDICTIONS_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioWithInvoicesRoute &&
      forecastType === ForecastType.FromLYA &&
      Number(onboardingStep) < Number(SCENARIO_WITH_INVOICES_ONBOARDING_STEP.LYA_CONTROL_CASH_FLOW)) ||
    (isOnboardingScenarioRoute && onboardingStep === SCENARIO_ONBOARDING_STEP.SELECT_TYPE) ||
    (isOnboardingScenarioWithInvoicesRoute && onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SELECT_TYPE);

  return (
    <div className={headerRowClasses} style={wrapperStyle} role="row">
      <div
        role="cell"
        className={headerRowInnerClasses}
        ref={refGroupingAccountSelector}
        data-testid="header-row-grouping-hover-anchor"
        style={{
          background: headerBackground,
          borderRadius: headerBackground ? '0px' : undefined,
          marginLeft: level * MARGIN_LEFT_LEVEL
        }}
      >
        <div className={clsx(grid.flexContainer, grid.flexContainerJustify)}>
          <HeaderRowAccountTitle row={row} isExpandedRow={isExpandedRow(index)} showDeferVat={showDeferVat} />
          <div className={clsx(grid.financialsRowHeaderActions, grid.rowHeaderActions)} style={{ height: '100%' }}>
            {showDaysToPay && (
              <div className={styles.daysToPayContainer} data-testid="days-to-pay">
                <div className={styles.daysToPayItem}>{hideDaysToPayValue ? '' : averageDaysToPay}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
